import { useMemo } from "react";

import { useAuthContext } from "@covertree/auth";
import { useWholesalerContext } from "@providers/WholesalerProvider";

/**
 * Defines the access patterns for user based on the user's permissions (groups).
 *
 * @returns - object with properties that defines the access patterns.
 */
export interface UseAccessPattern {
  canSeeCommissionablePartners: boolean;
  canSeeDiscardedRenewals: boolean;
  canWriteBusiness: boolean;
  canSeeOverviewMetricsDetailedData: boolean;
  isInternalAgent: boolean;
  isSugAgent: boolean;
  canMarkRenewalAsPaid: boolean;
  canRetryFailedRenewal: boolean;
  canSeePaymentDetailsData: boolean;
  canSeeInspectionOrderingDocument: boolean;
  canRemovePolicyDocument: boolean;
  canCreateSequoiaQuotes: boolean;
  canEditPolicyholder: boolean;
  canSeeAdditionalInterestTab: boolean;
  canManageAdditionalInterests: boolean;
  canSeeEndorsementsTab: boolean;
  canManageEndorsements: boolean;
}

const POLICY_TAB_WRITE_GROUPS = [
  "ct_master_agent",
  "ct_agent",
  "ct_service_agent",
  "master_agent",
  "binding_helping_agent",
  "agent",
  "service_agent",
];

const POLICY_TAB_READ_GROUPS = [
  ...POLICY_TAB_WRITE_GROUPS,
  "ct_admin_agent",
  "admin_agent",
];

export const useAccessPattern = (): UseAccessPattern => {
  const { isWholesaler } = useWholesalerContext();
  const { hasGroup, basicUserData } = useAuthContext();

  const agencyId = basicUserData?.agencyId;
  const wholesalerId = basicUserData?.wholesalerId;

  const isInternalAgent = useMemo(
    () =>
      hasGroup([
        "ct_service_agent",
        "ct_master_agent",
        "ct_agent",
        "ct_admin_agent",
      ]),
    [hasGroup]
  );

  const canSeeCommissionablePartners = useMemo(
    () =>
      hasGroup(["ct_master_agent", "ct_admin_agent", "ct_service_agent"]) ||
      (hasGroup([
        "master_agent",
        "binding_helping_agent",
        "admin_agent",
        "service_agent",
      ]) &&
        isWholesaler),
    [hasGroup, isWholesaler]
  );

  const canSeeDiscardedRenewals = useMemo(
    () => hasGroup(["ct_master_agent", "ct_admin_agent", "ct_service_agent"]),
    [hasGroup]
  );

  const canWriteBusiness = useMemo(
    () =>
      hasGroup([
        "ct_agent",
        "ct_master_agent",
        "agent",
        "binding_helping_agent",
        "master_agent",
      ]),
    [hasGroup]
  );

  const canEditPolicyholder = useMemo(
    () =>
      hasGroup([
        "ct_agent",
        "ct_master_agent",
        "agent",
        "binding_helping_agent",
        "master_agent",
      ]),
    [hasGroup]
  );

  const canSeeOverviewMetricsDetailedData = useMemo(
    () => !hasGroup(["ct_service_agent", "service_agent"]),
    [hasGroup]
  );

  const canMarkRenewalAsPaid = useMemo(
    () => hasGroup(["ct_master_agent", "ct_admin_agent", "ct_service_agent"]),
    [hasGroup]
  );

  const canRetryFailedRenewal = useMemo(
    () => isInternalAgent,
    [isInternalAgent]
  );

  const canSeePaymentDetailsData = useMemo(
    () => hasGroup(["ct_master_agent", "ct_admin_agent", "ct_service_agent"]),
    [hasGroup]
  );

  const canSeeInspectionOrderingDocument = useMemo(
    () => hasGroup(["ct_master_agent", "ct_admin_agent", "ct_service_agent"]),
    [hasGroup]
  );

  const canRemovePolicyDocument = useMemo(
    () => hasGroup(["ct_master_agent"]),
    [hasGroup]
  );

  const canCreateSequoiaQuotes = useMemo(
    () => hasGroup(["super_admin"]),
    [hasGroup]
  );

  const canSeeAdditionalInterestTab = useMemo(
    () => hasGroup(POLICY_TAB_READ_GROUPS),
    [hasGroup]
  );

  const canManageAdditionalInterests = useMemo(
    () => hasGroup(POLICY_TAB_WRITE_GROUPS),
    [hasGroup]
  );

  const canSeeEndorsementsTab = useMemo(
    () => hasGroup(POLICY_TAB_READ_GROUPS),
    [hasGroup]
  );

  const canManageEndorsements = useMemo(
    () => hasGroup(POLICY_TAB_WRITE_GROUPS),
    [hasGroup]
  );

  const isSugAgent = useMemo(
    () => agencyId === "C-009272" || wholesalerId === "C-009272",
    [agencyId, wholesalerId]
  );

  return useMemo(
    () => ({
      canMarkRenewalAsPaid,
      canSeeCommissionablePartners,
      canSeeDiscardedRenewals,
      canWriteBusiness,
      canSeeOverviewMetricsDetailedData,
      isInternalAgent,
      canRetryFailedRenewal,
      canSeePaymentDetailsData,
      canSeeInspectionOrderingDocument,
      canRemovePolicyDocument,
      canCreateSequoiaQuotes,
      canEditPolicyholder,
      isSugAgent,
      canSeeAdditionalInterestTab,
      canManageAdditionalInterests,
      canSeeEndorsementsTab,
      canManageEndorsements,
    }),
    [
      canMarkRenewalAsPaid,
      canSeeCommissionablePartners,
      canSeeDiscardedRenewals,
      canWriteBusiness,
      canSeeOverviewMetricsDetailedData,
      isInternalAgent,
      canRetryFailedRenewal,
      canSeePaymentDetailsData,
      canSeeInspectionOrderingDocument,
      canRemovePolicyDocument,
      canCreateSequoiaQuotes,
      canEditPolicyholder,
      isSugAgent,
      canSeeAdditionalInterestTab,
      canManageAdditionalInterests,
      canSeeEndorsementsTab,
      canManageEndorsements,
    ]
  );
};
